/**
 * Verantwortungslandkarte — data module (DX-180).
 *
 * The 11 areas of responsibility across the 4 fields, transcribed from the
 * Figma board "Verantwortungsbereiche" (as of 2026-06-26).
 *
 * **Interim solution.** DX-180 brings the map into the portal so the
 * information is available at all; the eventual home for it may look quite
 * different — most likely driven off catalog entities (`Group`/`Domain` with
 * real `spec.owner` relations) rather than a hand-maintained array. Content
 * therefore lives here, fully decoupled from the rendering components, so a
 * later rewrite replaces the UI and reads this array as its input instead of
 * having to re-transcribe the board.
 *
 * The content is deliberately kept in **German**: it is an internal org
 * document and its wording ("Verantwortungsbereich", "Gehört dazu") is the
 * vocabulary the team actually uses. The surrounding code and comments stay
 * English, as everywhere else in this repo.
 *
 * This module is the single source of truth for the map inside Backstage.
 * Editing an owner, a scope or a backup happens here and nowhere else — the
 * page and the home-page summary both render from this array.
 *
 * The `field` values double as the filter chips and must match a key of
 * `RESPONSIBILITY_FIELDS`.
 */

/** The four organisational fields the areas are grouped into. */
export type ResponsibilityField =
  | 'Platform Engineering'
  | 'Product Engineering'
  | 'Developer Experience'
  | 'Consulting';

/**
 * Visual identity per field. Only the *mapping* (field → distinct hue) is
 * load-bearing; the literal values follow whatever surface they sit on.
 *
 * Re-picked for the light content area (DX-275). The previous set was chosen
 * for the cockpit's dark glass and does not survive the move: `#FFF200` on
 * white is illegible, and the other three were bright enough to need a dark
 * ground. These are darkened to hold their hue against white while staying
 * distinguishable from each other, and the violet stays closest to the brand
 * because Platform is the field the portal itself belongs to.
 */
export const RESPONSIBILITY_FIELDS: Record<
  ResponsibilityField,
  { color: string; sidebarIllustration: string }
> = {
  'Platform Engineering': { color: '#620DFF', sidebarIllustration: '3d-3' },
  'Product Engineering': { color: '#1667C7', sidebarIllustration: '2d-15' },
  'Developer Experience': { color: '#B26A00', sidebarIllustration: '3d-12' },
  Consulting: { color: '#0F7A4D', sidebarIllustration: '3d-8' },
};

/** Field order for chips and grouped views — mirrors the sidebar order. */
export const RESPONSIBILITY_FIELD_ORDER: ResponsibilityField[] = [
  'Platform Engineering',
  'Product Engineering',
  'Developer Experience',
  'Consulting',
];

export type ResponsibilityArea = {
  field: ResponsibilityField;
  /** Short name of the area, e.g. "Infrastruktur". */
  bereich: string;
  /** One-line clarification shown under the name. */
  bereichDesc: string;
  /** Full name of the accountable person. */
  owner: string;
  mission: string;
  /** What belongs to this area. */
  scopeIn: string;
  /** What explicitly does *not* belong to it — the boundary that matters. */
  scopeOut: string;
  /** Guiding questions the owner is expected to answer. */
  questions: string[];
  /** Skills the area requires (not necessarily the owner's own). */
  skills: string[];
  /** Rationale for this owner holding this area. */
  why: string;
  /** Full names of the stand-ins; rendered as jump links to their areas. */
  backup: string[];
};

/** Stand of the source board, shown in the page header. */
export const RESPONSIBILITY_MAP_AS_OF = '26.06.2026';

/** The source board this map is transcribed from. */
export const RESPONSIBILITY_MAP_SOURCE_URL =
  'https://www.figma.com/board/mfTGre3Pmb8bDLjke733sX/Verantwortungsbereiche?node-id=0-1';

export const responsibilityAreas: ResponsibilityArea[] = [
  {
    field: 'Platform Engineering',
    bereich: 'Platform',
    bereichDesc: 'Kernsysteme der Plattform',
    owner: 'Tilmann Schäfer',
    mission:
      'Weiterentwicklung der Kernsysteme: Plattform (Appwrite-Fork), API Gateway, Postgres-Cluster, Event Bus und Kern des Integration Studio.',
    scopeIn:
      'Appwrite-Fork, API Gateway, Postgres-Cluster, Event Bus, Integration-Studio-Kern.',
    scopeOut: 'Frontends, Infrastruktur/DevOps außerhalb des Kerns, Apps.',
    questions: [
      'Wie entwickeln wir die Kernsysteme robust und skalierbar weiter?',
      'Welche Architekturentscheidungen sind tragfähig?',
    ],
    skills: [
      'Distributed-Systems- & Backend-Architektur',
      'tiefe Postgres-/Cluster-Kenntnisse',
      'Design von API-Gateway und Event-Bus',
      'Internals von Appwrite/BaaS',
      'Skalierbarkeits-, Performance- und Stabilitäts-Engineering',
    ],
    why: 'Langjähriger Produktarchitekt der alten Software-Generation; hohes Abstraktionsvermögen und tiefes Wissen zur Weiterentwicklung solcher Kernsysteme.',
    backup: ['Jonathan Kraut'],
  },
  {
    field: 'Platform Engineering',
    bereich: 'Infrastruktur',
    bereichDesc: 'Infrastruktur & Observability, DevOps',
    owner: 'Jonathan Kraut',
    mission:
      'Betrieb und Weiterentwicklung aller Systeme außerhalb der Kern-Plattform: Container, CDN, Monitoring/Observability.',
    scopeIn:
      'Container-Stack, CDN/Edge, Monitoring & Observability, Analytics-Infra, SFTP-/VPN-Verbindungen.',
    scopeOut: 'Kern-Plattform-Services, Produkt-Frontends.',
    questions: [
      'Wie setzen wir Observability & Monitoring richtig auf?',
      'Wie betreiben wir Container/CDN zuverlässig und sicher?',
      'Wann muss die Plattform vergrößert werden — und wie?',
      'Wie mache ich das kostensensibel und effizient?',
    ],
    skills: [
      'Container-Orchestrierung (Docker Swarm/Hetzner)',
      'CI/CD-Betrieb',
      'Observability-Stack (Logging, Metrics, Tracing)',
      'CDN-/Edge-Betrieb',
      'Netzwerk & Security (SFTP, VPN)',
      'Kapazitätsplanung & kostensensible Skalierung',
    ],
    why: 'Starkes DevOps-Verständnis und Weitblick; aus der Entwickler-Rolle (IntelliShop 8) kommend, treibt seit Monaten die richtigen DevOps-Themen.',
    backup: ['Tilmann Schäfer'],
  },
  {
    field: 'Product Engineering',
    bereich: 'UX: Experience Studio (Themes)',
    bereichDesc: 'Shop & Storefront (Frontend & Conversion)',
    owner: 'Michael Wachs',
    mission:
      'Beste Shop-UX im B2B-Commerce für maximale Conversion. Eine Storefront, die B2C-Komfort in den B2B-Kontext überträgt.',
    scopeIn:
      'Shop-Frontend, Product Discovery, Checkout-Flow, kundenspezifische Preisdarstellung, Such-Integration (Typesense).',
    scopeOut: 'Cockpit-/Admin-UI, Backend-Pricing-Logik, PIM-Datenmodell.',
    questions: [
      'Wie erreichen wir Best-in-Class Conversion im B2B?',
      'Welche bewährten B2C-Patterns lassen sich übertragen?',
      'Wie messen wir UX- und Conversion-Erfolg?',
    ],
    skills: [
      'Modernes Frontend-Engineering (Component-Architektur, Core Web Vitals)',
      'B2B-Commerce- & Conversion-Know-how (Product Discovery, Checkout-UX)',
      'A/B-Testing & Web-Analytics',
      'Anbindung von Such- und Pricing-APIs (Typesense, kundenspezifische Preise)',
    ],
    why: 'Jahrelange B2C-Frontend-Erfahrung (Magenta), Conversion-Optimierung & UX; überträgt B2C-Best-Practices in den B2B-Bereich.',
    backup: ['Patrick Gräf'],
  },
  {
    field: 'Product Engineering',
    bereich: 'UX: Cockpit & Blokkli',
    bereichDesc: 'Cockpit & Administration (Management-UX)',
    owner: 'Patrick Gräf',
    mission:
      'Beste UX für Administration und Management der Revenue Cloud aus Sicht unserer Kunden.',
    scopeIn: 'Cockpit-Frontend, Admin-/Management-Oberflächen, Studio UI Kit.',
    scopeOut: 'Shop-Storefront, Core-Plattform-Services, Infrastruktur.',
    questions: [
      'Wie wird Administration für Kunden intuitiv und self-service-fähig?',
      'Welche Management-Workflows haben Priorität?',
    ],
    skills: [
      'Full-Stack-Fähigkeit von API bis Frontend',
      'Aufbau & Pflege eines Design-/UI-Kits (Studio UI Kit)',
      'UX für komplexe Admin-, Management- und Daten-Oberflächen',
      'Konzepte für Rollen/Rechte (RBAC) und Mandantenfähigkeit',
      'Migration bestehender Console-Komponenten',
    ],
    why: 'Hat bei Magenta einen exzellenten Admin geowned & gebaut; bespielt den Stack von API bis Frontend; starke Produktideen.',
    backup: ['Michael Wachs'],
  },
  {
    field: 'Product Engineering',
    bereich: 'Schnittstellen: Integration Studio',
    bereichDesc: 'Integrationen zu Drittsystemen (ERP, PIM, CRM)',
    owner: 'Christopher Voitus',
    mission:
      'Config-driven, multi-tenant Integrationsplattform. Aufsetzen und Bauen von Integrationen zwischen Revenue Cloud und externen Systemen. Wir fangen an, wo andere Shopsysteme aufhören.',
    scopeIn:
      'Integration Studio, ERP-/PIM-/CRM-Anbindungen, Daten-Flows, Connector-Konfiguration.',
    scopeOut: 'Core-Plattform-Kernsysteme, App-Entwicklung, Frontends.',
    questions: [
      'Wie integrieren wir ohne per-Integration-Code und ohne Deployment?',
      'Wie skalieren Integrationen bei Multi-Tenant?',
      'Wo ist die Grenze zwischen Integration Studio und Custom App?',
      'Wie baue ich aus B2B-Sicht sinnvolle Schnittstellen auf — Beispiel: Preis-Live-Call ja, Abfrage von Mitarbeiterdaten aus dem ERP nein?',
    ],
    skills: [
      'Integrationsarchitektur für ERP/PIM/CRM',
      'sichere API-, Webhook- & EDI-Protokolle',
      'config-driven, multi-tenant Design ohne per-Integration-Code',
      'Daten-Mapping/-Transformation',
      'Event-/Messaging-Patterns',
      'B2B-Prozessverständnis zur fachlichen Abgrenzung',
    ],
    why: 'Hat die mit Abstand größten und umfangreichsten Integrationen in den Projekten umgesetzt.',
    backup: ['Tilmann Schäfer', 'Martin Mohr'],
  },
  {
    field: 'Product Engineering',
    bereich: 'AI: AI Studio',
    bereichDesc:
      'Künstliche Intelligenz (AI), KI-Level: AI Assistant, AI CoPilot, AI AutoPilot, AI AgentTeam',
    owner: 'Clemens Bastian',
    mission:
      'AI als Kernthema vorantreiben. Kontinuierlich am Markt bleiben und die richtigen AI-Bausteine zum richtigen Zeitpunkt aufsetzen.',
    scopeIn:
      'AI-Strategie & -Architektur, (self-hosted) Inference-System, Modelle, MCP, System-Prompts, AI-Agenten.',
    scopeOut: 'klassisches BI-Reporting, Dateninfrastruktur-Betrieb.',
    questions: [
      'Wie baue ich ein Agenten-System, das skaliert und für den Endkunden funktioniert?',
      'Wann lohnt ein eigenes (self-hosted) Inference-System?',
      'Was sind die besten Modelle für uns?',
      'Wann welche Modelle/Agenten — und wie setzen wir sie um?',
    ],
    skills: [
      'AI-/ML-Engineering',
      'LLM- & Agenten-Architektur (RAG, MCP, System-Prompts)',
      'Inference-Hosting (self-hosted vs. managed)',
      'Model-Evaluation & -Auswahl',
      'produktive Skalierung von AI für Endkunden',
      'Steuerung von Kosten-/Latenz-Trade-offs',
    ],
    why: 'Principal Architect der Projektwelt mit tiefem Software-Architektur-Wissen; sehr hohes Vertrauen für das Thema AI.',
    backup: ['Martin Mohr', 'Tilmann Schäfer'],
  },
  {
    field: 'Product Engineering',
    bereich: 'Services',
    bereichDesc:
      'Business-Applikationen (B2B) — Querschnitt von Services über RAG bis hin zu Apps',
    owner: 'Martin Mohr',
    mission:
      'Bau unserer Applikationen und Zusatzservices Richtung Business-Kunden.',
    scopeIn: 'B2B-Apps, kleine Zusatz-Services, kundennahe Anwendungen.',
    scopeOut: 'Kern-Plattform, DX/SDKs, Infrastruktur.',
    questions: [
      'Welche Apps und Services brauchen unsere B2B-Kunden wirklich?',
      'Wie rollen wir sie richtig aus?',
      'Was muss als eigenständiger Service etabliert und bereitgestellt werden?',
    ],
    skills: [
      'App- & Service-Entwicklung mit B2B-Fokus',
      'Produktdenken & B2B-Domänenwissen',
      'API-Design für Zusatz-Services',
      'Rollout-/Release-Management',
      'saubere Abgrenzung App vs. eigenständiger Service',
    ],
    why: 'Hervorragender Entwickler mit ausgeprägtem B2B-Verständnis und dem Willen zum konsequenten Rollout.',
    backup: ['Tilmann Schäfer'],
  },
  {
    field: 'Developer Experience',
    bereich: 'Developer Portal & SDKs',
    bereichDesc: 'SDKs & externe Developer Experience',
    owner: 'Alexander Bystrow',
    mission:
      'Bester SDK- und externer DX-Stack aus Entwicklersicht; Entwickler befähigen und onboarden.',
    scopeIn: 'SDKs, DX-Stack, Entwickler-Onboarding & Dokumentation.',
    scopeOut: 'interne DX-Systeme, App-Entwicklung, Kern-Plattform.',
    questions: [
      'Wie onboarden wir externe Entwickler am schnellsten?',
      'Wie gut sind SDKs und Doku?',
    ],
    skills: [
      'SDK-/API-Design (mehrsprachig)',
      'Technical Writing & strukturierte Dokumentation',
      'Aufbau eines Developer-Portals',
      'Onboarding-/Tutorial-Didaktik',
      'Developer-Support & Community',
    ],
    why: 'Sehr gute Erklär- und Onboarding-Fähigkeiten; war bereits Onboarding-Verantwortlicher in der IntelliShop-Welt.',
    backup: ['Elmar Hepp'],
  },
  {
    field: 'Developer Experience',
    bereich: 'Backstage & internes DX-Tooling (JAM)',
    bereichDesc: 'Interne DX & Tooling',
    owner: 'Elmar Hepp',
    mission:
      'Unsere internen DX-Systeme sind für jeden Entwickler leicht verständlich und machen Spaß.',
    scopeIn: 'Backstage, CI/CD-Pipelines, GitHub & interne Dev-Tools.',
    scopeOut: 'externe SDKs/DX, App-Entwicklung, Produkt-Frontends.',
    questions: [
      'Wie machen wir interne Tools so verständlich, dass jeder im Team sie nutzen kann?',
    ],
    skills: [
      'Internal Developer Platform (Backstage)',
      'CI/CD-Pipeline-Engineering',
      'GitHub-Governance (Actions, Templates, Standards)',
      'Golden Paths & Self-Service',
      'Doku & Wissenstransfer ins Team',
    ],
    why: 'Verständnis- und didaktikorientiert; Autodidakt par excellence — Elmar wird es so einfach machen, dass es jeder versteht.',
    backup: ['Alexander Bystrow'],
  },
  {
    field: 'Consulting',
    bereich: 'Consulting',
    bereichDesc:
      'Consulting & Kundenbetreuung (perspektivisch: spezialisierte B2B-Apps)',
    owner: 'Johannes Heizmann',
    mission:
      'Strategischer Auf- und Umbau des Consulting-Bereichs zum besten Consulting für die Revenue Cloud.',
    scopeIn:
      'Consulting-Strategie, Kunden- & Projektbetreuung, Aufbau der Consulting-Organisation.',
    scopeOut: 'Produktentwicklung, Plattform/Infrastruktur.',
    questions: [
      'Wie bauen wir Consulting strategisch auf?',
      'Wie betreuen wir Kunden und Projekte exzellent?',
    ],
    skills: [
      'Projekt-/Programm-Management',
      'Kundenberatung & Stakeholder-Management',
      'Revenue-Cloud- & Domänen-Know-how',
      'Aufbau und Skalierung einer Consulting-Organisation',
      'Pre-Sales/Solutioning',
    ],
    why: 'Exzellenter Projektleiter; herausragende Kunden- und Projektbetreuung; enorme Erfahrung für B2B-Prozesse und Usability; Liebe zum Detail.',
    backup: ['Wolfram Schmale'],
  },
  {
    field: 'Consulting',
    bereich: 'App Studio',
    bereichDesc: 'Apps im Ökosystem (deklarative App-Entwicklung)',
    owner: 'Wolfram Schmale',
    mission:
      'Unsere langjährige B2B-DNA in Apps überführen — aus der Praxis für die Praxis. Über die deklarative App-Entwicklung selbst Apps bauen, veröffentlichen und weiterentwickeln.',
    scopeIn:
      'Ökosystem-Apps, deklarative App-Entwicklung, Veröffentlichung & Weiterentwicklung.',
    scopeOut: 'Kern-Plattform-Infrastruktur, externe Integrationen.',
    questions: [
      'Welche Apps treiben unser Ökosystem?',
      'Wie nutzen wir die deklarative Entwicklung optimal?',
    ],
    skills: [
      'Deklarative App-Entwicklung (Schema-as-Code)',
      'Produkt-Ownership in PIM-/Commerce-Domäne',
      'App-Lifecycle (Build, Publish, Iterate)',
      'Daten-Modellierung',
      'Ökosystem-/Marketplace-Denken',
    ],
    why: 'Langjähriger Product Owner IntelliShop 8; tiefes Produkt- und Domänenwissen; große Erfahrung für B2B-Prozesse.',
    backup: ['Johannes Heizmann'],
  },
];

/**
 * Stable DOM id / anchor for an area, derived from its owner — the source HTML
 * keyed anchors off the owner name so a backup button could jump to that
 * person's own area, and the backup jump links here rely on the same mapping.
 *
 * Umlauts are transliterated rather than stripped so "Tilmann Schäfer" and a
 * hypothetical "Tilmann Schafer" cannot collide on the same id.
 */
export const areaAnchorId = (ownerName: string): string =>
  `verantwortung-${ownerName
    .toLowerCase()
    .replace(/[äöüß]/g, m => ({ ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' })[m]!)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;

/** First name, used for the "Skills von <name>" heading. */
export const firstName = (fullName: string): string => fullName.split(' ')[0];

/**
 * The path each field is reachable at, and the short label the navigation uses
 * for it (DX-275).
 *
 * The sidebar used to send every field's entries into the catalog filtered by a
 * grouping the catalog does not have, so all of them arrived empty. These paths
 * are the landing pages that replace those links: one place per field that says
 * what the field covers and who holds it.
 */
export const FIELD_ROUTES: Record<
  ResponsibilityField,
  { path: string; label: string }
> = {
  'Platform Engineering': { path: '/platform', label: 'Platform' },
  'Product Engineering': { path: '/product', label: 'Product' },
  'Developer Experience': { path: '/dx', label: 'DX' },
  Consulting: { path: '/consulting', label: 'Consulting' },
};

/** The field a landing-page path belongs to, or undefined for a stray path. */
export const fieldForPath = (path: string): ResponsibilityField | undefined =>
  RESPONSIBILITY_FIELD_ORDER.find(f => FIELD_ROUTES[f].path === path);

/** Areas grouped by field, in `RESPONSIBILITY_FIELD_ORDER`. */
export const areasByField = (): {
  field: ResponsibilityField;
  areas: ResponsibilityArea[];
}[] =>
  RESPONSIBILITY_FIELD_ORDER.map(field => ({
    field,
    areas: responsibilityAreas.filter(a => a.field === field),
  })).filter(group => group.areas.length > 0);
